/**
 * Appfolio-specific utilities
 * PMS-specific parsing and transformation logic
 */

import { normalizeUSPhoneNumber } from '@super/shared-types'
import { isValidEmail } from '../utils/email-utils'
import type { PhoneNumber, PhoneType } from '@super/shared-types'

/**
 * Known column names used for phone numbers in Appfolio and Appfolio-compatible CSV exports.
 * Each entry declares the column header and the phone type to assign when the value is a plain number.
 * The "Phone Numbers" column uses Appfolio's "Type: Number" prefix format and is parsed accordingly;
 * all other columns are treated as single plain numbers with the declared type.
 */
export const APPFOLIO_PHONE_COLUMN_ALIASES: {
  column: string
  type: PhoneType
}[] = [
  { column: 'Phone Numbers', type: 'phone' },
  { column: 'Phone', type: 'phone' },
  { column: 'Cell', type: 'mobile' },
  { column: 'Home', type: 'phone' },
  { column: 'Office', type: 'office' },
]

/**
 * Collect phone numbers from all known phone columns in a CSV row.
 * Combines results from every present alias column, deduplicating by normalized number.
 */
export function collectAppfolioPhones(
  row: Record<string, string | undefined>,
): PhoneNumber[] {
  const seen = new Set<string>()
  const result: PhoneNumber[] = []

  for (const alias of APPFOLIO_PHONE_COLUMN_ALIASES) {
    const value = row[alias.column]
    if (!value?.trim()) continue

    const phones =
      alias.column === 'Phone Numbers'
        ? parseAppfolioPhoneNumbers(value)
        : parsePlainPhone(value, alias.type)

    for (const phone of phones) {
      if (!seen.has(phone.number)) {
        seen.add(phone.number)
        result.push(phone)
      }
    }
  }

  return result
}

function parsePlainPhone(value: string, type: PhoneType): PhoneNumber[] {
  const normalized = normalizeUSPhoneNumber(value.trim())
  return normalized ? [{ number: normalized, type }] : []
}

/**
 * Parse multiple phone numbers from Appfolio format
 * Input: "Phone: (757) 478-2261, Phone: (757) 337-3120, Mobile: +17579061588"
 * Output: Array of PhoneNumber objects with normalized numbers and types
 */
export function parseAppfolioPhoneNumbers(phoneField?: string): PhoneNumber[] {
  if (!phoneField?.trim()) return []

  const phoneNumbers: PhoneNumber[] = []

  // Split by commas and process each entry
  const entries = phoneField.split(',').map(entry => entry.trim())

  for (const entry of entries) {
    const phoneData = extractSingleAppfolioPhone(entry)
    if (phoneData) {
      phoneNumbers.push(phoneData)
    }
  }

  return phoneNumbers
}

/**
 * Extract single phone number with type from Appfolio format
 * Input: "Mobile: (757) 317-1821" or "Phone: (757) 478-2261"
 * Output: {number: "+17573171821", type: "mobile"} or null
 */
function extractSingleAppfolioPhone(entry: string): PhoneNumber | null {
  if (!entry?.trim()) return null

  // Extract type and number
  const typeMatch = entry.match(/^(Phone|Mobile|Office):\s*(.+)$/i)
  if (!typeMatch) {
    // Try without prefix - just normalize the number, default to 'phone' type
    const normalized = normalizeUSPhoneNumber(entry)
    return normalized ? { number: normalized, type: 'phone' } : null
  }

  const [, typeStr, numberStr] = typeMatch
  const normalized = normalizeUSPhoneNumber(numberStr)

  if (!normalized) return null

  // Map Appfolio types to our enum
  const type: PhoneType =
    typeStr.toLowerCase() === 'mobile'
      ? 'mobile'
      : typeStr.toLowerCase() === 'office'
        ? 'office'
        : 'phone'

  return { number: normalized, type }
}

/**
 * Parse multiple emails from Appfolio format
 * Input: "bobbyt77@yahoo.com, Robert.T77@outlook.com"
 * Output: ["bobbyt77@yahoo.com", "Robert.T77@outlook.com"]
 */
export function parseAppfolioEmails(emailField?: string): string[] {
  if (!emailField?.trim()) return []

  return emailField
    .split(',')
    .map(email => email.trim())
    .filter(email => email && isValidEmail(email))
}

/**
 * Get primary phone from parsed phone numbers
 * Priority: first mobile > first phone > first office > first any
 */
export function getPrimaryPhone(phoneNumbers: PhoneNumber[]): {
  number?: string
  type?: PhoneType
} {
  if (phoneNumbers.length === 0) return {}

  // Try to find mobile first
  const mobile = phoneNumbers.find(p => p.type === 'mobile')
  if (mobile) return { number: mobile.number, type: mobile.type }

  // Then phone
  const phone = phoneNumbers.find(p => p.type === 'phone')
  if (phone) return { number: phone.number, type: phone.type }

  // Then office
  const office = phoneNumbers.find(p => p.type === 'office')
  if (office) return { number: office.number, type: office.type }

  // Finally, first available
  const first = phoneNumbers[0]
  return { number: first.number, type: first.type }
}

/**
 * Get alternate phone numbers (excluding the primary)
 */
export function getAlternatePhones(
  phoneNumbers: PhoneNumber[],
  primaryNumber?: string,
): PhoneNumber[] {
  if (!primaryNumber) return phoneNumbers

  return phoneNumbers.filter(p => p.number !== primaryNumber)
}

/**
 * Get primary email from parsed emails
 * Simply returns the first valid email
 */
export function getPrimaryEmail(emails: string[]): string | undefined {
  return emails[0]
}

/**
 * Get alternate emails (excluding the primary)
 */
export function getAlternateEmails(
  emails: string[],
  primaryEmail?: string,
): string[] {
  if (!primaryEmail) return emails

  return emails.filter(email => email !== primaryEmail)
}

/**
 * Parse Appfolio tags field
 * Input: "tag1, tag2, tag3" or "single-tag"
 * Output: ["tag1", "tag2", "tag3"] or undefined if empty
 */
export function parseAppfolioTags(tagsField?: string): string[] | undefined {
  if (!tagsField?.trim()) return undefined

  return tagsField
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean)
}

/**
 * Build a stable property composite key from individual CSV fields.
 * Format: "<name> - <street> <city>, <state> <zip>"
 * Scoped by companyId at query time so company name is not needed.
 */
export function buildPropertyComposite(
  name: string,
  street: string,
  city: string,
  state: string,
  zip: string,
): string {
  return `${name} - ${street} ${city}, ${state} ${zip}`
}

/**
 * Extract the composite key from AppFolio's `Property` column.
 * The column value is already in "<name> - <address>" composite format — return as-is.
 * Returns undefined if no ' - ' separator is present (not a valid composite).
 */
export function extractPropertyCompositeFromColumn(
  col: string,
): string | undefined {
  if (!col.includes(' - ')) return undefined
  return col.trim()
}

/**
 * Parse combined name field from Appfolio format
 * Input: "[LastName], [FirstName]" or "Smith, John"
 * Output: { firstName: "John", lastName: "Smith" } or null
 *
 * Note: Only handles single comma format. Names with multiple commas
 * (e.g., "Smith, Jr., John") are not standard AppFolio format and will return null.
 */
export function parseCombinedName(nameField?: string): {
  firstName?: string
  lastName?: string
} | null {
  if (!nameField?.trim()) return null

  const parts = nameField
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)

  if (parts.length === 2 && parts[0] && parts[1]) {
    return {
      lastName: parts[0],
      firstName: parts[1],
    }
  }

  if (parts.length === 1 && parts[0]) {
    return {
      lastName: parts[0],
      firstName: undefined,
    }
  }

  return null
}
